"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircle, XCircle, AlertCircle, ExternalLink, ShieldCheck } from "lucide-react";
import { formatDate } from "@/lib/formatting";
import { useDealApiMutation } from "@/lib/deals/useDealApiMutation";
import { BillingAddressCard } from "@/components/deals/shared/BillingAddressCard";
import type { DealVerification, IdentityStatus } from "@/lib/deals/verification";
import type { DealContractReview } from "@/lib/deals/contract-review/types";
import { dealDocumentUrl } from "@/lib/deals/document-download/refs";
interface AdminIdentityPanelProps {
  dealId: string;
  verification: DealVerification | null;
  contractReview: DealContractReview | null;
  onStatusChange?: (verification: DealVerification) => void;
  className?: string;
}
function IdentityDocumentRow(t0) {
  const $ = _c(39);
  const {
    label,
    documentUrl,
    downloadHref,
    documentName,
    uploadedAt,
    status,
    onVerify,
    onReject,
    isSubmitting
  } = t0;
  if (!documentUrl) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ShieldCheck className="w-5 h-5 text-zinc-300" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== label) {
      t2 = <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"><div className="flex items-center gap-3">{t1}<span className="text-sm text-zinc-400">{label}: Nicht hochgeladen</span></div></div>;
      $[1] = label;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  const t1 = `w-5 h-5 ${status === "verified" ? "text-emerald-500" : status === "rejected" ? "text-red-500" : "text-blue-500"}`;
  let t2;
  if ($[3] !== t1) {
    t2 = <ShieldCheck className={t1} />;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = documentName || "Ausweisdokument";
  let t4;
  if ($[5] !== label || $[6] !== t3) {
    t4 = <p className="text-sm font-medium text-zinc-700">{label}: {t3}</p>;
    $[5] = label;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== uploadedAt) {
    t5 = formatDate(uploadedAt);
    $[8] = uploadedAt;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = status === "verified" ? "text-emerald-600" : status === "rejected" ? "text-red-600" : status === "uploaded" ? "text-blue-600" : "text-zinc-400";
  const t7 = status === "verified" && "Verifiziert";
  const t8 = status === "rejected" && "Abgelehnt";
  const t9 = status === "uploaded" && "Pr\xFCfung ausstehend";
  const t10 = status === "pending" && "Ausstehend";
  let t11;
  if ($[10] !== t10 || $[11] !== t6 || $[12] !== t7 || $[13] !== t8 || $[14] !== t9) {
    t11 = <span className={t6}>{t7}{t8}{t9}{t10}</span>;
    $[10] = t10;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t5) {
    t12 = <p className="text-xs text-zinc-500">{t5}{" \xB7 "}{t11}</p>;
    $[16] = t11;
    $[17] = t5;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== t4) {
    t13 = <div>{t4}{t12}</div>;
    $[19] = t12;
    $[20] = t4;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== t2) {
    t14 = <div className="flex items-center gap-3">{t2}{t13}</div>;
    $[22] = t13;
    $[23] = t2;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ExternalLink className="w-3.5 h-3.5" />;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== downloadHref) {
    t16 = <a href={downloadHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">Öffnen{t15}</a>;
    $[26] = downloadHref;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== isSubmitting || $[29] !== onReject || $[30] !== onVerify || $[31] !== status) {
    t17 = (status === "uploaded" || status === "pending") && <><button type="button" onClick={onVerify} disabled={isSubmitting} className="p-1.5 rounded bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50" title="Verifizieren"><CheckCircle className="w-4 h-4" /></button><button type="button" onClick={onReject} disabled={isSubmitting} className="p-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50" title="Ablehnen"><XCircle className="w-4 h-4" /></button></>;
    $[28] = isSubmitting;
    $[29] = onReject;
    $[30] = onVerify;
    $[31] = status;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t16 || $[34] !== t17) {
    t18 = <div className="flex items-center gap-2">{t16}{t17}</div>;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t14 || $[37] !== t18) {
    t19 = <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">{t14}{t18}</div>;
    $[36] = t14;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  return t19;
}

/**
 * AdminIdentityPanel - Admin panel for verifying buyer/seller identity documents
 */
export function AdminIdentityPanel(t0) {
  const $ = _c(48);
  const {
    dealId,
    verification,
    contractReview,
    onStatusChange,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  let t2;
  if ($[0] !== onStatusChange) {
    t2 = {
      onSuccess: data => onStatusChange?.(data)
    };
    $[0] = onStatusChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    mutate,
    isSubmitting,
    error
  } = useDealApiMutation(t2);
  const sellerIdentityStatus = (verification?.seller_identity_status ?? "pending") as IdentityStatus;
  const hasSellerIdentity = !!verification?.seller_identity_document_url;
  const hasBillingInfo = !!(contractReview?.seller_billing_name || contractReview?.buyer_billing_name);
  if (!hasSellerIdentity && !hasBillingInfo) {
    const t3 = `rounded-lg border border-zinc-200 bg-zinc-50 p-4 ${className}`;
    let t4;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="flex items-center gap-3 text-zinc-500"><AlertCircle className="w-5 h-5" /><p className="text-sm">Noch keine Ausweisdokumente hochgeladen</p></div>;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    let t5;
    if ($[3] !== t3) {
      t5 = <div className={t3}>{t4}</div>;
      $[3] = t3;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    return t5;
  }
  let t3;
  if ($[5] !== dealId || $[6] !== mutate) {
    t3 = (role, status) => {
      mutate(`/api/admin/deals/${dealId}/verification`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          role,
          status
        })
      });
    };
    $[5] = dealId;
    $[6] = mutate;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleIdentityAction = t3;
  const t4 = `space-y-2 ${className}`;
  const t5 = contractReview?.buyer_billing_company;
  const t6 = contractReview?.buyer_billing_name;
  const t7 = contractReview?.buyer_billing_address_line1;
  const t8 = contractReview?.buyer_billing_address_line2;
  const t9 = contractReview?.buyer_billing_postal_code;
  const t10 = contractReview?.buyer_billing_city;
  const t11 = contractReview?.buyer_billing_phone;
  const t12 = contractReview?.buyer_billing_tax_id;
  let t13;
  if ($[8] !== t10 || $[9] !== t11 || $[10] !== t12 || $[11] !== t5 || $[12] !== t6 || $[13] !== t7 || $[14] !== t8 || $[15] !== t9) {
    t13 = <BillingAddressCard address={{
      company: t5,
      name: t6,
      addressLine1: t7,
      addressLine2: t8,
      postalCode: t9,
      city: t10,
      phone: t11,
      taxId: t12
    }} label={"K\xE4ufer"} role="buyer" variant="compact" emptyText={"Adressdaten noch nicht ausgef\xFCllt"} />;
    $[8] = t10;
    $[9] = t11;
    $[10] = t12;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== dealId) {
    t14 = dealDocumentUrl(dealId, "identity-seller");
    $[17] = dealId;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const t15 = verification?.seller_identity_document_url as string | undefined;
  const t16 = verification?.seller_identity_document_name as string | undefined;
  const t17 = verification?.seller_identity_uploaded_at as string | undefined;
  let t18;
  let t19;
  if ($[19] !== handleIdentityAction) {
    t18 = () => handleIdentityAction("seller", "verified");
    t19 = () => handleIdentityAction("seller", "rejected");
    $[19] = handleIdentityAction;
    $[20] = t18;
    $[21] = t19;
  } else {
    t18 = $[20];
    t19 = $[21];
  }
  let t20;
  if ($[22] !== isSubmitting || $[23] !== sellerIdentityStatus || $[24] !== t14 || $[25] !== t15 || $[26] !== t16 || $[27] !== t17 || $[28] !== t18 || $[29] !== t19) {
    t20 = <IdentityDocumentRow label={"Verk\xE4ufer"} downloadHref={t14} documentUrl={t15} documentName={t16} uploadedAt={t17} status={sellerIdentityStatus} onVerify={t18} onReject={t19} isSubmitting={isSubmitting} />;
    $[22] = isSubmitting;
    $[23] = sellerIdentityStatus;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  const t21 = contractReview?.seller_billing_company;
  const t22 = contractReview?.seller_billing_name;
  const t23 = contractReview?.seller_billing_address_line1;
  const t24 = contractReview?.seller_billing_address_line2;
  const t25 = contractReview?.seller_billing_postal_code;
  const t26 = contractReview?.seller_billing_city;
  const t27 = contractReview?.seller_billing_phone;
  const t28 = contractReview?.seller_billing_tax_id;
  let t29;
  if ($[31] !== t21 || $[32] !== t22 || $[33] !== t23 || $[34] !== t24 || $[35] !== t25 || $[36] !== t26 || $[37] !== t27 || $[38] !== t28) {
    t29 = <BillingAddressCard address={{
      company: t21,
      name: t22,
      addressLine1: t23,
      addressLine2: t24,
      postalCode: t25,
      city: t26,
      phone: t27,
      taxId: t28
    }} label={"Verk\xE4ufer"} role="seller" variant="compact" emptyText={"Adressdaten noch nicht ausgef\xFCllt"} />;
    $[31] = t21;
    $[32] = t22;
    $[33] = t23;
    $[34] = t24;
    $[35] = t25;
    $[36] = t26;
    $[37] = t27;
    $[38] = t28;
    $[39] = t29;
  } else {
    t29 = $[39];
  }
  let t30;
  if ($[40] !== error) {
    t30 = error && <div className="flex items-center gap-2 text-sm text-red-600"><AlertCircle className="w-4 h-4" /><span>{error}</span></div>;
    $[40] = error;
    $[41] = t30;
  } else {
    t30 = $[41];
  }
  let t31;
  if ($[42] !== t13 || $[43] !== t20 || $[44] !== t29 || $[45] !== t30 || $[46] !== t4) {
    t31 = <div className={t4}>{t13}{t20}{t29}{t30}</div>;
    $[42] = t13;
    $[43] = t20;
    $[44] = t29;
    $[45] = t30;
    $[46] = t4;
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  return t31;
}